import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { DictionaryIrregularVerb } from 'app/types';
import { LevelPill } from './primitives';

interface Props {
  verbs: readonly DictionaryIrregularVerb[];
}

const thSx = {
  py: 1.25,
  pt: 1.5,
  px: 1.75,
  fontSize: 11.5,
  fontWeight: 800,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  textAlign: 'left' as const,
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const tdSx = {
  py: 1.5,
  px: 1.75,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const speak = (word: string) => (e: React.MouseEvent) => {
  e.stopPropagation();
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(word);
  u.lang = 'en-US';
  u.rate = 0.9;
  window.speechSynthesis.speak(u);
};

const FormCell: React.FC<{ word: string; transcription?: string; strong?: boolean }> = ({
  word,
  transcription,
  strong,
}) => (
  <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>
    <Box sx={{ fontSize: 15.5, fontWeight: strong ? 700 : 600, color: 'text.primary' }}>{word}</Box>
    {transcription ? (
      <Box sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{transcription}</Box>
    ) : null}
  </TableCell>
);

const IrregularVerbsTable: React.FC<Props> = ({ verbs }) => {
  const [hover, setHover] = useState<number>(-1);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Table sx={{ width: '100%', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell sx={{ ...thSx, width: 44 }}>№</TableCell>
            <TableCell sx={{ ...thSx, width: 64 }}>Рівень</TableCell>
            <TableCell sx={thSx}>Інфінітив</TableCell>
            <TableCell sx={thSx}>Past Simple</TableCell>
            <TableCell sx={thSx}>Past Participle</TableCell>
            <TableCell sx={{ ...thSx, width: '28%' }}>Переклад</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {verbs.map((v, i) => (
            <TableRow
              key={v.id}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(-1)}
              sx={{
                bgcolor: hover === i ? 'primary.body' : 'transparent',
                transition: 'background .12s ease',
              }}
            >
              <TableCell sx={{ ...tdSx, fontSize: 13.5, color: 'text.secondary' }}>
                {i + 1}
              </TableCell>
              <TableCell sx={tdSx}>
                {v.levelBased ? <LevelPill level={v.levelBased} /> : null}
              </TableCell>
              <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box>
                    <Box sx={{ fontSize: 15.5, fontWeight: 800, color: 'primary.main' }}>
                      {v.infinitive}
                    </Box>
                    {v.infinitiveTranscription ? (
                      <Box sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
                        {v.infinitiveTranscription}
                      </Box>
                    ) : null}
                  </Box>
                  {hover === i && (
                    <IconButton
                      size="small"
                      onClick={speak(v.infinitive)}
                      title={`Вимова: ${v.infinitive}`}
                      sx={{ ml: 0.25, color: 'text.secondary' }}
                    >
                      <VolumeUpIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
              <FormCell word={v.pastSimple} transcription={v.pastSimpleTranscription} />
              <FormCell word={v.pastParticiple} transcription={v.pastParticipleTranscription} />
              <TableCell sx={{ ...tdSx, fontSize: 14.5, color: 'text.primary' }}>
                {v.translation}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {verbs.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', fontSize: 14 }}>
          Нічого не знайдено
        </Box>
      ) : null}
    </Box>
  );
};

export default IrregularVerbsTable;
